import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Stack, Link, CircularProgress, Alert, Chip,
} from '@mui/material';
import {
  UploadFile as UploadIcon, PictureAsPdf as PdfIcon, Image as ImageIcon,
  Delete as DeleteIcon, Visibility as ViewIcon,
} from '@mui/icons-material';
import api from '../../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

/**
 * Attach / view PDF (and image) files against a Purchase Order. Used on the PO
 * edit page and the PO overview. Files are stored server-side; view/download
 * streams the file with auth via a blob URL (so it works with the token header).
 * (Rishabh 2026-07-01.)
 */
export default function PoDocuments({ poId, canEdit = true, dense = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    try {
      const r = await api.get(`${API_BASE}/api/procurement/purchase-orders/${poId}/documents`);
      setDocs(r.data?.data || []);
      setError(null);
    } catch (e) {
      setError('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  }, [poId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !poId) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true); setError(null);
    try {
      await api.post(`${API_BASE}/api/procurement/purchase-orders/${poId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (doc) => {
    try {
      const r = await api.get(
        `${API_BASE}/api/procurement/purchase-orders/${poId}/documents/${doc.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError('Failed to open document');
    }
  };

  const handleDelete = async (doc) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${doc.file_name}"?`)) return;
    try {
      await api.delete(`${API_BASE}/api/procurement/purchase-orders/${poId}/documents/${doc.id}`);
      await load();
    } catch (e) {
      setError('Delete failed');
    }
  };

  if (!poId) {
    return <Typography variant="caption" color="text.secondary">Save the PO first to attach documents.</Typography>;
  }

  const isPdf = (m) => (m || '').includes('pdf');

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant={dense ? 'body2' : 'subtitle2'} fontWeight={600}>
          Attachments {docs.length > 0 && <Chip label={docs.length} size="small" sx={{ ml: 0.5 }} />}
        </Typography>
        {loading && <CircularProgress size={14} />}
        <Box sx={{ flex: 1 }} />
        {canEdit && (
          <Button
            component="label"
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon fontSize="small" />}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : 'Add PDF'}
            <input hidden type="file" accept="application/pdf,image/png,image/jpeg" onChange={handleUpload} />
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {docs.length === 0 && !loading ? (
        <Typography variant="caption" color="text.secondary">No files attached yet.</Typography>
      ) : (
        <Stack spacing={0.5}>
          {docs.map((d) => (
            <Stack key={d.id} direction="row" alignItems="center" spacing={1}
              sx={{ p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
              {isPdf(d.mime) ? <PdfIcon color="error" fontSize="small" /> : <ImageIcon color="action" fontSize="small" />}
              <Link component="button" type="button" underline="hover" onClick={() => handleView(d)}
                sx={{ flex: 1, textAlign: 'left', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.file_name}
              </Link>
              <Typography variant="caption" color="text.secondary">
                {d.size_bytes ? `${(d.size_bytes / 1024).toFixed(0)} KB` : ''}
              </Typography>
              <IconButton size="small" onClick={() => handleView(d)} title="View"><ViewIcon fontSize="small" /></IconButton>
              {canEdit && (
                <IconButton size="small" color="error" onClick={() => handleDelete(d)} title="Remove"><DeleteIcon fontSize="small" /></IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}
